/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { useCallback, useRef, useState } from 'react';
import { JiraEntryPointContainer } from '@jira/platform__entry-points-container/src/index.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import { useEntryPoint } from '@jira/platform__entry-point/src/controllers/use-entry-point/index.tsx';
import { useEntryPointButtonTrigger } from '@jira/platform__entry-point-button-trigger/src/index.tsx';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ListBulletedIcon from '@atlaskit/icon/core/list-bulleted';

import ButtonItem from '@atlaskit/menu/button-item';
import { token } from '@atlaskit/tokens';
import mergeRefs from '@atlassian/jira-merge-refs/src/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { JiraPopup as Popup } from '@atlassian/jira-popup/src/ui/jira-popup.tsx';

import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { Skeleton } from '../skeleton/index.tsx';
import type { ChangeViewMenuItemProps } from './types.tsx';
import { ProjectsViewOptionsEntrypoint } from './projects-view-options/ProjectsViewOptionsEntrypoint.entrypoint.tsx';

const changeViewMenuItemLabel = defineMessage({
	id: 'navigation-apps-sidebar-nav4-display-settings.change-view-action.change-view-menu-item-label',

	defaultMessage: 'Change view',
	description: 'Used as the button label text for the "Change view" menu item',
});

const listBulletedIcon = defineMessage({
	id: 'navigation-apps-sidebar-nav4-display-settings.change-view-action.list-bulleted-icon',

	defaultMessage: 'List bulleted icon',
	description: 'The label for the list bulleted icon',
});

const chevronRightIcon = defineMessage({
	id: 'navigation-apps-sidebar-nav4-display-settings.change-view-action.chevron-right-icon',

	defaultMessage: 'Chevron right icon',
	description: 'The label for the chevron right icon',
});

const FOCUS_RETURNING_KEYS = ['Tab', 'Escape', 'Esc'];

export function ChangeViewMenuItem({
	onOpen,
	onChange,
}: ChangeViewMenuItemProps): React.JSX.Element {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const cloudId = useCloudId();

	const onChangeViewMenuItemClick = () => {
		if (!isOpen && onOpen) {
			onOpen();
		}
		setIsOpen(!isOpen);
	};
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		ProjectsViewOptionsEntrypoint,
		{
			cloudId,
			onChange,
		},
	);

	const entrypointRef = useEntryPointButtonTrigger(entryPointActions);
	const triggerRef = useRef<HTMLElement>(null);

	// Disabling the focus lock also disables AtlasKit's focus restoration, so the trigger reclaims it.
	const onPopupClose = useCallback(
		(event: Event | React.MouseEvent | React.KeyboardEvent | null) => {
			setIsOpen(false);
			if (event === null || !('key' in event) || !FOCUS_RETURNING_KEYS.includes(event.key)) {
				return;
			}
			// Tab would otherwise move focus onwards before the trigger reclaims it.
			if (event.key === 'Tab') {
				event.preventDefault();
			}
			triggerRef.current?.focus();
		},
		[],
	);

	return (
		<Popup
			isOpen={isOpen}
			onClose={onPopupClose}
			content={() => (
				<JiraEntryPointContainer
					entryPointReferenceSubject={entryPointReferenceSubject}
					id="display-settings-menu-popup-content"
					packageName="jira-navigation-apps-sidebar-nav4-display-settings"
					fallback={<Skeleton />}
					runtimeProps={{}}
					errorFallback="flag"
				/>
			)}
			placement="right-start"
			// eslint-disable-next-line jira/choreographer/message-id-by-folder-structure
			messageId="navigation-apps-sidebar-nav4-display-settings.ui.change-view-menu-item.popup"
			messageType="transactional"
			shouldRenderToParent
			{...(expVal('sea-3302-arrow-key-nav-proj-sidebar-view-options', 'isEnabled', false)
				? { shouldDisableFocusLock: true }
				: {})}
			trigger={({ ref, ...triggerProps }) => (
				<ButtonItem
					{...triggerProps}
					ref={mergeRefs(ref, entrypointRef, triggerRef)}
					isSelected={isOpen}
					iconBefore={
						<ListBulletedIcon
							label={formatMessage(listBulletedIcon)}
							spacing="spacious"
							color={token('color.icon')}
						/>
					}
					iconAfter={<ChevronRightIcon label={formatMessage(chevronRightIcon)} size="small" />}
					onClick={onChangeViewMenuItemClick}
					aria-label={formatMessage(changeViewMenuItemLabel)}
					aria-expanded={isOpen}
					aria-haspopup="true"
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					testId="navigation-apps-sidebar-nav4-display-settings.ui.change-view-menu-item.button"
				>
					{formatMessage(changeViewMenuItemLabel)}
				</ButtonItem>
			)}
		/>
	);
}
